
import React, { useState, useEffect } from 'react';
import { UserProfile, CoreValue } from '../types';
import { generateDeepQuestion, analyzeCoreValues, generateSpiritualPractice } from '../services/geminiService';
import { Compass, Feather, Heart, Sun, Moon, Wind, Loader2, Send, Bookmark, Sparkles, Plus, BookOpen, Quote } from 'lucide-react';
import { TRANSLATIONS } from '../translations';

interface Props {
  user: UserProfile;
  onUpdateUser: (user: UserProfile) => void;
}

const PhilosophyHub: React.FC<Props> = ({ user, onUpdateUser }) => {
  const [activeTab, setActiveTab] = useState<'values' | 'sanctuary'>('values');
  const t = TRANSLATIONS[user.language];
  
  // Values Discovery State
  const [currentQuestion, setCurrentQuestion] = useState<string | null>(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [loadingQuestion, setLoadingQuestion] = useState(false);
  const [analyzingAnswer, setAnalyzingAnswer] = useState(false);

  // Sanctuary State
  const [practiceType, setPracticeType] = useState<'meditation' | 'reflection' | null>(null);
  const [practiceContent, setPracticeContent] = useState('');
  const [loadingPractice, setLoadingPractice] = useState(false);
  const [gratitudeInput, setGratitudeInput] = useState('');

  // Initial load of question if none exists
  useEffect(() => {
    if (activeTab === 'values' && !currentQuestion && user.philosophyProfile.coreValues.length < 5) {
      loadNewQuestion();
    }
  }, [activeTab]);

  const loadNewQuestion = async () => {
    setLoadingQuestion(true);
    try {
      const q = await generateDeepQuestion(user);
      setCurrentQuestion(q);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingQuestion(false);
    }
  };

  const handleAnswerSubmit = async () => {
    if (!userAnswer.trim() || !currentQuestion) return;
    setAnalyzingAnswer(true);
    try {
      const newValues = await analyzeCoreValues(user, currentQuestion, userAnswer);
      
      const updatedUser = { ...user };
      updatedUser.philosophyProfile.coreValues = newValues;
      updatedUser.philosophyProfile.answeredQuestions.push({
        question: currentQuestion,
        answer: userAnswer,
        timestamp: Date.now()
      });

      onUpdateUser(updatedUser);
      setUserAnswer('');
      setCurrentQuestion(null);
      setTimeout(loadNewQuestion, 1000); 

    } catch (e) {
      console.error(e);
    } finally {
      setAnalyzingAnswer(false);
    }
  };

  const handleGeneratePractice = async (type: 'meditation' | 'reflection') => {
    setPracticeType(type);
    setLoadingPractice(true);
    setPracticeContent('');
    try {
      const content = await generateSpiritualPractice(user, type);
      setPracticeContent(content);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPractice(false);
    }
  };

  return (
    <div className="h-full flex flex-col animate-fade-in pb-20">
      <div className="flex items-center justify-between mb-6">
        <div>
           <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
             <Compass className="w-6 h-6 text-indigo-600" />
             {t.philosophy_hub}
           </h2>
           <p className="text-slate-500 text-sm mt-1">{t.philosophy_hub_desc}</p>
        </div>
        <div className="flex bg-white rounded-xl border border-slate-200 p-1">
            <button 
                onClick={() => setActiveTab('values')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'values' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-500'}`}>
                {user.language === 'ckb' ? 'بەهاکان' : 'ارزش‌ها'}
            </button>
            <button 
                onClick={() => setActiveTab('sanctuary')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'sanctuary' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-500'}`}>
                {user.language === 'ckb' ? 'خلوتگە' : 'خلوتگاه'}
            </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'values' && (
          <div className="space-y-8">
            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500 opacity-20"></div>
                <h3 className="font-black text-slate-800 mb-6 flex items-center gap-3 text-lg">
                    <Feather className="w-6 h-6 text-indigo-500" />
                    سیستەمی ناسینەوەی بەهاکان
                </h3>
                
                {user.philosophyProfile?.coreValues?.length === 0 ? (
                    <div className="p-10 text-center border-2 border-dashed border-slate-100 rounded-3xl">
                        <Sparkles className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                        <p className="text-slate-400 font-medium">بەهاکانی تۆ نهێنین؛ بە وەڵامدانەوەی پرسیارەکان بمدۆزەوە.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        {user.philosophyProfile?.coreValues?.map(val => (
                            <div key={val.id} className="bg-gradient-to-br from-indigo-50 to-white border border-indigo-100 p-5 rounded-2xl hover:shadow-md transition-shadow group">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="p-2 bg-white rounded-xl shadow-sm text-xl group-hover:scale-110 transition-transform">{val.icon || '💎'}</div>
                                    <span className="font-black text-indigo-800">{val.label}</span>
                                </div>
                                <p className="text-xs text-slate-500 leading-relaxed mb-3 line-clamp-2">{val.description}</p>
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                        <div className="h-full bg-indigo-500" style={{ width: `${val.score}%` }}></div>
                                    </div>
                                    <span className="text-[10px] font-black text-indigo-400">{val.score}%</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="bg-slate-900 rounded-[3rem] p-10 text-white shadow-2xl relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500 rounded-full blur-[120px] opacity-10 -mr-32 -mt-32"></div>
                <div className="relative z-10 max-w-2xl mx-auto w-full text-center">
                    <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-xs font-black mb-8 border border-white/10 tracking-widest uppercase">
                        <Wind className="w-4 h-4 text-indigo-300" />
                        گەشتی قووڵبوونەوە
                    </div>
                    
                    {loadingQuestion ? (
                        <div className="flex flex-col items-center py-12">
                            <Loader2 className="w-12 h-12 animate-spin text-indigo-400 mb-4" />
                            <p className="text-indigo-200 animate-pulse">خەریکم پرسیارێکی مانا-تەوەر دادەڕێژم...</p>
                        </div>
                    ) : (
                        <h3 className="text-2xl md:text-4xl font-black leading-tight font-serif mb-10 text-indigo-50">
                            "{currentQuestion}"
                        </h3>
                    )}

                    {!loadingQuestion && (
                        <div className="space-y-6">
                            <textarea 
                                value={userAnswer}
                                onChange={(e) => setUserAnswer(e.target.value)}
                                placeholder="بە ڕاشکاوی بیرۆکەکانت بنووسە..."
                                className="w-full bg-white/5 border border-white/10 rounded-[2rem] p-6 text-white placeholder:text-white/30 focus:outline-none focus:bg-white/10 focus:ring-4 focus:ring-indigo-500/20 transition-all resize-none h-40 text-lg leading-relaxed shadow-inner"
                            />
                            <button 
                                onClick={handleAnswerSubmit}
                                disabled={!userAnswer.trim() || analyzingAnswer}
                                className="bg-white text-indigo-900 px-12 py-4 rounded-2xl font-black text-lg hover:bg-indigo-50 transition-all shadow-xl shadow-white/10 disabled:opacity-50 flex items-center gap-3 mx-auto active:scale-95"
                            >
                                {analyzingAnswer ? <Loader2 className="animate-spin w-6 h-6" /> : <Send className="w-6 h-6 rtl:-rotate-90" />}
                                {analyzingAnswer ? 'خەریکی شیکاری...' : 'تۆمارکردنی تێڕوانین'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
          </div>
        )}

        {activeTab === 'sanctuary' && (
          <div className="space-y-8 animate-fade-in">
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 <button 
                    onClick={() => handleGeneratePractice('meditation')}
                    className="bg-white hover:bg-slate-50 border border-slate-100 p-8 rounded-[2.5rem] text-right transition-all group shadow-sm hover:shadow-xl"
                 >
                     <div className="bg-teal-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 text-teal-600 shadow-sm group-hover:rotate-6 transition-transform">
                         <Sun className="w-8 h-8" />
                     </div>
                     <h4 className="font-black text-slate-800 text-xl mb-2">مدیتیشنی ژیری</h4>
                     <p className="text-slate-500 text-sm leading-relaxed"> Gemini ژوورێکی تایبەت بە مدیتیشن بۆ مێشکت دادەنێت بەپێی باری ئێستات.</p>
                 </button>

                 <button 
                    onClick={() => handleGeneratePractice('reflection')}
                    className="bg-white hover:bg-slate-50 border border-slate-100 p-8 rounded-[2.5rem] text-right transition-all group shadow-sm hover:shadow-xl"
                 >
                     <div className="bg-indigo-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 text-indigo-600 shadow-sm group-hover:-rotate-6 transition-transform">
                         <Moon className="w-8 h-8" />
                     </div>
                     <h4 className="font-black text-slate-800 text-xl mb-2">تێڕامانی ڕۆژانە</h4>
                     <p className="text-slate-500 text-sm leading-relaxed">بەهاکانت تاقیدەکەینەوە؛ ئایا کارەکانی ئەمڕۆت هاوتای بەهاکانت بوون؟</p>
                 </button>
             </div>

             <div className="bg-amber-50 p-8 rounded-[2.5rem] border border-amber-100 shadow-inner">
                <div className="flex items-center gap-3 mb-6 text-amber-800">
                    <Quote className="w-6 h-6 opacity-50" />
                    <h3 className="font-black text-lg">{t.philosophy_stoic_quote}</h3>
                </div>
                <p className="text-xl font-serif text-amber-900 leading-loose italic">
                    "ژیان زۆر کورتە بۆ ئەوەی بە نیگەرانی دەربارەی ئەو شتانە بەسەر بەرین کە لەژێر دەستی ئێمەدا نین."
                </p>
             </div>

             {(loadingPractice || practiceContent) && (
                 <div className="bg-white border border-slate-100 p-10 rounded-[3rem] shadow-2xl animate-slide-up relative overflow-hidden">
                     <div className="absolute top-0 right-0 p-10 opacity-5 pointer-events-none"><Compass className="w-64 h-64" /></div>
                     
                     {loadingPractice ? (
                         <div className="flex flex-col items-center justify-center py-20">
                             <Loader2 className="w-12 h-12 animate-spin text-indigo-200 mb-6" />
                             <p className="text-slate-400 font-bold">خەریکم خلوتگەیەک بۆ ڕۆحت دروست دەکەم...</p>
                         </div>
                     ) : (
                         <div className="max-w-3xl mx-auto">
                             <div className="flex items-center gap-3 mb-10 text-slate-400 text-xs font-black uppercase tracking-[0.2em] border-b pb-4">
                                 <Sparkles className="w-4 h-4 text-amber-500" />
                                 {practiceType === 'meditation' ? 'ئەزموونی مدیتیشن' : 'تێڕامانی فەلسەفی'}
                             </div>
                             <div className="whitespace-pre-wrap font-serif text-xl leading-[2.2] text-slate-700 text-justify">
                                 {practiceContent}
                             </div>
                         </div>
                     )}
                 </div>
             )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhilosophyHub;
